/**
 * @zh 平台适配的 Worker 池管理器
 * @en Platform-adapted Worker Pool Manager
 */
// =============================================================================
// 常量 | Constants
// =============================================================================
const ERROR_POOL_DESTROYED = 'Worker pool has been destroyed';
// =============================================================================
// PlatformWorkerPool
// =============================================================================
/**
 * @zh 平台适配的 Worker 池管理器
 * @en Platform-adapted Worker Pool Manager
 *
 * @zh 管理 Worker 生命周期、任务分发和状态跟踪
 * @en Manages Worker lifecycle, task distribution and state tracking
 */
export class PlatformWorkerPool {
    // =========================================================================
    // 构造函数 | Constructor
    // =========================================================================
    /**
     * @zh 创建 Worker 池
     * @en Create Worker pool
     *
     * @param workers - @zh Worker 实例数组 @en Array of Worker instances
     * @param sharedBuffer - @zh 共享内存缓冲区 @en Shared memory buffer
     */
    constructor(workers, sharedBuffer) {
        this.workerStates = new Map();
        this.pendingTasks = new Map();
        this.taskQueue = [];
        this.taskCounter = 0;
        this._isDestroyed = false;
        this.workers = workers;
        this.initializeWorkers(sharedBuffer);
    }
    // =========================================================================
    // 公共属性 | Public Properties
    // =========================================================================
    /**
     * @zh 池是否已销毁
     * @en Whether the pool has been destroyed
     */
    get isDestroyed() {
        return this._isDestroyed;
    }
    /**
     * @zh Worker 数量
     * @en Number of workers in the pool
     */
    get workerCount() {
        return this.workers.length;
    }
    /**
     * @zh 所有 Worker 是否已就绪（无初始化中的 Worker）
     * @en Whether all workers are ready (no initializing workers)
     */
    get isReady() {
        if (this._isDestroyed)
            return false;
        for (const state of this.workerStates.values()) {
            if (state === 0 /* WorkerState.Initializing */)
                return false;
        }
        return this.workers.length > 0;
    }
    /**
     * @zh 是否有待处理的任务（队列中或执行中）
     * @en Whether there are pending tasks (queued or executing)
     */
    get hasPendingTasks() {
        return this.taskQueue.length > 0 || this.pendingTasks.size > 0;
    }
    // =========================================================================
    // 公共方法 | Public Methods
    // =========================================================================
    /**
     * @zh 执行 SharedArrayBuffer 任务
     * @en Execute SharedArrayBuffer task
     *
     * @param data - @zh 任务数据 @en Task data
     * @returns @zh 任务完成的 Promise @en Promise that resolves when task completes
     */
    executeSharedBuffer(data) {
        return this.createTask(`shared-${++this.taskCounter}`, { ...data, type: 'shared' }, () => undefined);
    }
    /**
     * @zh 执行普通任务
     * @en Execute normal task
     *
     * @param data - @zh 任务数据 @en Task data
     * @returns @zh 包含任务结果的 Promise @en Promise with task result
     */
    execute(data) {
        return this.createTask(`task-${++this.taskCounter}`, data, (result) => result);
    }
    /**
     * @zh 获取 Worker 池状态
     * @en Get Worker pool status
     *
     * @returns @zh 池状态对象 @en Pool status object
     */
    getStatus() {
        const status = { idle: 0, busy: 0, initializing: 0 };
        for (const state of this.workerStates.values()) {
            if (state === 1 /* WorkerState.Idle */)
                status.idle++;
            else if (state === 2 /* WorkerState.Busy */)
                status.busy++;
            else if (state === 0 /* WorkerState.Initializing */)
                status.initializing++;
        }
        return {
            total: this.workers.length,
            ...status,
            queuedTasks: this.taskQueue.length
        };
    }
    /**
     * @zh 销毁 Worker 池，释放所有资源
     * @en Destroy Worker pool and release all resources
     */
    destroy() {
        if (this._isDestroyed)
            return;
        this._isDestroyed = true;
        const destroyError = new Error(ERROR_POOL_DESTROYED);
        // Reject all pending and queued tasks
        for (const task of this.pendingTasks.values()) {
            task.reject(destroyError);
        }
        for (const task of this.taskQueue) {
            task.reject(destroyError);
        }
        // Terminate all workers
        for (const worker of this.workers) {
            worker.terminate();
        }
        // Clear state
        this.workers.length = 0;
        this.taskQueue.length = 0;
        this.pendingTasks.clear();
        this.workerStates.clear();
    }
    // =========================================================================
    // 私有方法 | Private Methods
    // =========================================================================
    /**
     * @zh 初始化所有 Worker
     * @en Initialize all Workers
     */
    initializeWorkers(sharedBuffer) {
        for (let i = 0; i < this.workers.length; i++) {
            const worker = this.workers[i];
            if (!worker)
                continue;
            this.workerStates.set(i, sharedBuffer ? 0 /* WorkerState.Initializing */ : 1 /* WorkerState.Idle */);
            worker.onMessage((event) => this.handleMessage(i, event.data));
            worker.onError((error) => this.handleError(i, error));
            if (sharedBuffer) {
                worker.postMessage({ type: 'init', sharedBuffer });
            }
        }
    }
    /**
     * @zh 创建并入队任务
     * @en Create and enqueue task
     */
    createTask(id, data, transform) {
        return new Promise((resolve, reject) => {
            if (this._isDestroyed) {
                reject(new Error(ERROR_POOL_DESTROYED));
                return;
            }
            this.enqueueTask({
                id,
                data,
                resolve: (result) => resolve(transform(result)),
                reject
            });
        });
    }
    /**
     * @zh 将任务加入队列
     * @en Enqueue task
     */
    enqueueTask(task) {
        this.taskQueue.push(task);
        this.dispatchTasks();
    }
    /**
     * @zh 分发任务到空闲 Worker
     * @en Dispatch tasks to idle Workers
     */
    dispatchTasks() {
        while (this.taskQueue.length > 0) {
            const workerIndex = this.findIdleWorker();
            if (workerIndex === -1)
                break;
            const task = this.taskQueue.shift();
            this.assignTask(workerIndex, task);
        }
    }
    /**
     * @zh 查找空闲 Worker
     * @en Find idle Worker
     */
    findIdleWorker() {
        for (let i = 0; i < this.workers.length; i++) {
            if (this.workerStates.get(i) === 1 /* WorkerState.Idle */) {
                return i;
            }
        }
        return -1;
    }
    /**
     * @zh 分配任务给指定 Worker
     * @en Assign task to specified Worker
     */
    assignTask(workerIndex, task) {
        const worker = this.workers[workerIndex];
        if (!worker)
            return;
        this.workerStates.set(workerIndex, 2 /* WorkerState.Busy */);
        this.pendingTasks.set(workerIndex, task);
        worker.postMessage({
            id: task.id,
            ...task.data
        });
    }
    /**
     * @zh 处理 Worker 消息
     * @en Handle Worker message
     */
    handleMessage(workerIndex, data) {
        if (data.type === 'init') {
            this.workerStates.set(workerIndex, 1 /* WorkerState.Idle */);
            this.dispatchTasks();
            return;
        }
        if (data.error) {
            this.completeTask(workerIndex, new Error(data.error));
        }
        else {
            this.completeTask(workerIndex, undefined, data.result);
        }
    }
    /**
     * @zh 处理 Worker 错误
     * @en Handle Worker error
     */
    handleError(workerIndex, error) {
        this.completeTask(workerIndex, new Error(error.message));
    }
    /**
     * @zh 完成任务并释放 Worker
     * @en Complete task and release Worker
     */
    completeTask(workerIndex, error, result) {
        const task = this.pendingTasks.get(workerIndex);
        if (!task)
            return;
        this.pendingTasks.delete(workerIndex);
        this.workerStates.set(workerIndex, 1 /* WorkerState.Idle */);
        if (error) {
            task.reject(error);
        }
        else {
            task.resolve(result);
        }
        this.dispatchTasks();
    }
}
//# sourceMappingURL=PlatformWorkerPool.js.map